/**
 * Maps URL slugs for the best-companies dimension pages to Typesense filter
 * expressions on the 'companies' index.
 *
 * Three dimension types:
 *  - Industry: filters on `industryEn`, unaffected by the location-system flag below.
 *  - City:     filters on `regionTags` (a gazetteer placeId, TEC-482 follow-up) when the new
 *              location system is enabled; falls back to the original exact
 *              `registryCity:"X"` string match otherwise (see `getDimensionConfig` below) — the
 *              old match silently missed any company whose registry city was spelled/cased
 *              differently than the curated string, `regionTags` doesn't have that gap.
 *  - Region:   filters on `regionTags` too, with a recruiting-region code (e.g. "NORDICS") or an
 *              admin1 placeId (e.g. Uusimaa) instead of a single city, genuinely new, not
 *              possible under the old exact-city-string model — so region dimensions simply
 *              don't exist when the flag is off (`getDimensionConfig` returns `null` for them).
 *
 * **Gated behind `dev_enable_new_location_system`** (TEC-482 follow-up), same as the rest of
 * this rollout — `getDimensionConfig`/`getCityDimensions`/`getRegionDimensions` all take the
 * flag explicitly rather than reading it themselves, so every caller has to pass it (thread it
 * from `readNewLocationSystemOptInFromCookieHeader` server-side or `useNewLocationSystemEnabled`
 * client-side; see the call sites in `+data.server.ts`/`load-best-companies-dimension-data.ts`/
 * `server/routes/best-companies-dimension.ts`/`+Page.tsx`). Originally shipped as a direct,
 * unconditional swap (reasoned as a strict improvement, since the old match had real bugs), but
 * that meant deploying this file's city/region dimensions ahead of a `generateCompanyScores` run
 * populating `regionTags` on companies would zero out every city/region card and page for real
 * users with no way to fall back — gating it removes that live-regression risk entirely: the
 * flag stays off in production until `regionTags` company data (and this UI) has actually been
 * verified, exactly like the job-search side of this rollout.
 */

export type DimensionType = 'industry' | 'city' | 'region';

export interface DimensionConfig {
  type: DimensionType;
  label: string; // display label, e.g. "Technology"
  algoliaFilter: string; // Typesense filter expression (field name kept from the pre-Typesense migration)
  metaTitleSuffix: string; // appended after "Best Employers in …"
  metaDescriptionFragment: string;
}

const DIMENSION_MAP: Record<string, DimensionConfig> = {
  // ── Industries ─────────────────────────────────────────────────────────────
  technology: {
    type: 'industry',
    label: 'Technology',
    algoliaFilter: 'industryEn:"Technology"',
    metaTitleSuffix: 'Technology',
    metaDescriptionFragment: 'the technology sector in Finland',
  },
  finance: {
    type: 'industry',
    label: 'Finance & Banking',
    algoliaFilter: 'industryEn:"Finance"',
    metaTitleSuffix: 'Finance & Banking',
    metaDescriptionFragment: 'finance and banking in Finland',
  },
  healthcare: {
    type: 'industry',
    label: 'Healthcare',
    algoliaFilter: 'industryEn:"Healthcare"',
    metaTitleSuffix: 'Healthcare',
    metaDescriptionFragment: 'the healthcare sector in Finland',
  },
  retail: {
    type: 'industry',
    label: 'Retail & E-commerce',
    algoliaFilter: 'industryEn:"Retail"',
    metaTitleSuffix: 'Retail & E-commerce',
    metaDescriptionFragment: 'retail and e-commerce in Finland',
  },
  manufacturing: {
    type: 'industry',
    label: 'Manufacturing & Engineering',
    algoliaFilter: 'industryEn:"Manufacturing"',
    metaTitleSuffix: 'Manufacturing & Engineering',
    metaDescriptionFragment: 'manufacturing and engineering in Finland',
  },
  consulting: {
    type: 'industry',
    label: 'Consulting & Professional Services',
    algoliaFilter: 'industryEn:"Consulting"',
    metaTitleSuffix: 'Consulting',
    metaDescriptionFragment: 'consulting and professional services in Finland',
  },
  education: {
    type: 'industry',
    label: 'Education & Research',
    algoliaFilter: 'industryEn:"Education"',
    metaTitleSuffix: 'Education & Research',
    metaDescriptionFragment: 'education and research organisations in Finland',
  },
  logistics: {
    type: 'industry',
    label: 'Logistics & Transport',
    algoliaFilter: 'industryEn:"Logistics"',
    metaTitleSuffix: 'Logistics & Transport',
    metaDescriptionFragment: 'logistics and transport companies in Finland',
  },
  media: {
    type: 'industry',
    label: 'Media & Marketing',
    algoliaFilter: 'industryEn:"Media"',
    metaTitleSuffix: 'Media & Marketing',
    metaDescriptionFragment: 'media and marketing companies in Finland',
  },
  // ── Cities (regionTags:<gazetteer placeId>, TEC-482 follow-up) ───────────────
  helsinki: {
    type: 'city',
    label: 'Helsinki',
    algoliaFilter: 'regionTags:658225',
    metaTitleSuffix: 'Helsinki',
    metaDescriptionFragment: 'Helsinki',
  },
  espoo: {
    type: 'city',
    label: 'Espoo',
    algoliaFilter: 'regionTags:660158',
    metaTitleSuffix: 'Espoo',
    metaDescriptionFragment: 'Espoo',
  },
  tampere: {
    type: 'city',
    label: 'Tampere',
    algoliaFilter: 'regionTags:634963',
    metaTitleSuffix: 'Tampere',
    metaDescriptionFragment: 'Tampere',
  },
  turku: {
    type: 'city',
    label: 'Turku',
    algoliaFilter: 'regionTags:633679',
    metaTitleSuffix: 'Turku',
    metaDescriptionFragment: 'Turku',
  },
  oulu: {
    type: 'city',
    label: 'Oulu',
    algoliaFilter: 'regionTags:643492',
    metaTitleSuffix: 'Oulu',
    metaDescriptionFragment: 'Oulu',
  },
  vantaa: {
    type: 'city',
    label: 'Vantaa',
    algoliaFilter: 'regionTags:632453',
    metaTitleSuffix: 'Vantaa',
    metaDescriptionFragment: 'Vantaa',
  },
  jyvaskyla: {
    type: 'city',
    label: 'Jyväskylä',
    algoliaFilter: 'regionTags:655194',
    metaTitleSuffix: 'Jyväskylä',
    metaDescriptionFragment: 'Jyväskylä',
  },
  lahti: {
    type: 'city',
    label: 'Lahti',
    algoliaFilter: 'regionTags:649360',
    metaTitleSuffix: 'Lahti',
    metaDescriptionFragment: 'Lahti',
  },
  kuopio: {
    type: 'city',
    label: 'Kuopio',
    algoliaFilter: 'regionTags:650224',
    metaTitleSuffix: 'Kuopio',
    metaDescriptionFragment: 'Kuopio',
  },
  pori: {
    type: 'city',
    label: 'Pori',
    algoliaFilter: 'regionTags:640999',
    metaTitleSuffix: 'Pori',
    metaDescriptionFragment: 'Pori',
  },
  lappeenranta: {
    type: 'city',
    label: 'Lappeenranta',
    algoliaFilter: 'regionTags:648900',
    metaTitleSuffix: 'Lappeenranta',
    metaDescriptionFragment: 'Lappeenranta',
  },
  kotka: {
    type: 'city',
    label: 'Kotka',
    algoliaFilter: 'regionTags:650946',
    metaTitleSuffix: 'Kotka',
    metaDescriptionFragment: 'Kotka',
  },
  joensuu: {
    type: 'city',
    label: 'Joensuu',
    algoliaFilter: 'regionTags:655808',
    metaTitleSuffix: 'Joensuu',
    metaDescriptionFragment: 'Joensuu',
  },
  rovaniemi: {
    type: 'city',
    label: 'Rovaniemi',
    algoliaFilter: 'regionTags:638936',
    metaTitleSuffix: 'Rovaniemi',
    metaDescriptionFragment: 'Rovaniemi',
  },
  seinajoki: {
    type: 'city',
    label: 'Seinäjoki',
    algoliaFilter: 'regionTags:637219',
    metaTitleSuffix: 'Seinäjoki',
    metaDescriptionFragment: 'Seinäjoki',
  },
  vaasa: {
    type: 'city',
    label: 'Vaasa',
    algoliaFilter: 'regionTags:632978',
    metaTitleSuffix: 'Vaasa',
    metaDescriptionFragment: 'Vaasa',
  },
  porvoo: {
    type: 'city',
    label: 'Porvoo',
    algoliaFilter: 'regionTags:660561',
    metaTitleSuffix: 'Porvoo',
    metaDescriptionFragment: 'Porvoo',
  },
  hameenlinna: {
    type: 'city',
    label: 'Hämeenlinna',
    algoliaFilter: 'regionTags:659180',
    metaTitleSuffix: 'Hämeenlinna',
    metaDescriptionFragment: 'Hämeenlinna',
  },
  mikkeli: {
    type: 'city',
    label: 'Mikkeli',
    algoliaFilter: 'regionTags:646005',
    metaTitleSuffix: 'Mikkeli',
    metaDescriptionFragment: 'Mikkeli',
  },
  kokkola: {
    type: 'city',
    label: 'Kokkola',
    algoliaFilter: 'regionTags:651943',
    metaTitleSuffix: 'Kokkola',
    metaDescriptionFragment: 'Kokkola',
  },
  kajaani: {
    type: 'city',
    label: 'Kajaani',
    algoliaFilter: 'regionTags:654899',
    metaTitleSuffix: 'Kajaani',
    metaDescriptionFragment: 'Kajaani',
  },
  rauma: {
    type: 'city',
    label: 'Rauma',
    algoliaFilter: 'regionTags:639734',
    metaTitleSuffix: 'Rauma',
    metaDescriptionFragment: 'Rauma',
  },
  lohja: {
    type: 'city',
    label: 'Lohja',
    algoliaFilter: 'regionTags:647751',
    metaTitleSuffix: 'Lohja',
    metaDescriptionFragment: 'Lohja',
  },
  nurmijarvi: {
    type: 'city',
    label: 'Nurmijärvi',
    algoliaFilter: 'regionTags:644171',
    metaTitleSuffix: 'Nurmijärvi',
    metaDescriptionFragment: 'Nurmijärvi',
  },
  // ── Regions (TEC-482 follow-up, genuinely new, not possible under the old exact-city
  //    string model: a company's registry address resolves through the full gazetteer
  //    hierarchy, so a region-level filter picks up every city within it) ─────────────────
  nordics: {
    type: 'region',
    label: 'Nordics',
    algoliaFilter: 'regionTags:NORDICS',
    metaTitleSuffix: 'the Nordics',
    metaDescriptionFragment: 'the Nordic region',
  },
  uusimaa: {
    type: 'region',
    label: 'Uusimaa',
    algoliaFilter: 'regionTags:830709',
    metaTitleSuffix: 'Uusimaa',
    metaDescriptionFragment: 'the Uusimaa region',
  },
  pirkanmaa: {
    type: 'region',
    label: 'Pirkanmaa',
    algoliaFilter: 'regionTags:830704',
    metaTitleSuffix: 'Pirkanmaa',
    metaDescriptionFragment: 'the Pirkanmaa region',
  },
};

/**
 * @param newLocationSystemEnabled `dev_enable_new_location_system` — required, no default, so
 * every call site has to actually thread the flag through rather than silently defaulting to
 * one behavior. City dimensions get `regionTags:<placeId>` when true, `registryCity:"<Label>"`
 * (the original filter, mechanically derived from `label`) when false. Region dimensions only
 * exist when true — they have no old-system equivalent.
 */
export function getDimensionConfig(slug: string, newLocationSystemEnabled: boolean): DimensionConfig | null {
  const config = DIMENSION_MAP[slug.toLowerCase()];
  if (!config) return null;
  if (newLocationSystemEnabled) return config;
  if (config.type === 'region') return null;
  if (config.type === 'city') return { ...config, algoliaFilter: `registryCity:"${config.label}"` };
  return config;
}

/** All configured dimensions available under the given flag state, used to build the hub page. */
export function getAllDimensions(
  newLocationSystemEnabled: boolean,
): Array<DimensionConfig & { slug: string }> {
  return Object.keys(DIMENSION_MAP)
    .map((slug) => {
      const config = getDimensionConfig(slug, newLocationSystemEnabled);
      return config ? { slug, ...config } : null;
    })
    .filter((d): d is DimensionConfig & { slug: string } => d !== null);
}

export function getCityDimensions(newLocationSystemEnabled: boolean): Array<DimensionConfig & { slug: string }> {
  return getAllDimensions(newLocationSystemEnabled).filter((d) => d.type === 'city');
}

export function getRegionDimensions(newLocationSystemEnabled: boolean): Array<DimensionConfig & { slug: string }> {
  return getAllDimensions(newLocationSystemEnabled).filter((d) => d.type === 'region');
}

/** Unaffected by the location-system flag — industries never used `regionTags`. */
export const INDUSTRY_DIMENSIONS = Object.entries(DIMENSION_MAP)
  .filter(([, config]) => config.type === 'industry')
  .map(([slug, config]) => ({ slug, ...config }));
